import React from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Chip,
  Typography,
} from '@mui/material';
import { Download, Delete, FolderOpen } from '@mui/icons-material';
import {
  EnhancedProjectFile,
  formatFileSize,
  CATEGORY_COLORS,
} from '../../types/file.types';

/**
 * FileListView Props
 */
interface FileListViewProps {
  /** 표시할 파일 목록 (이미 필터링됨) */
  files: EnhancedProjectFile[];
  /** 선택된 파일 ID 목록 */
  selectedFiles: string[];
  /** 파일 선택 핸들러 */
  onFileSelect: (fileId: string, selected: boolean) => void;
  /** 파일 클릭 핸들러 */
  onFileClick: (file: EnhancedProjectFile) => void;
  /** 다운로드 핸들러 (선택적) */
  onFileDownload?: (file: EnhancedProjectFile) => void;
  /** 삭제 핸들러 (선택적) */
  onFileDelete?: (file: EnhancedProjectFile) => void;
  /** 검색어 (빈 상태 메시지용) */
  searchTerm?: string;
}

/**
 * 파일 리스트 뷰 컴포넌트
 * 
 * @description
 * 파일을 테이블 형태로 표시
 * 
 * @columns
 * - 선택 체크박스
 * - 파일명 (최대 2줄, 줄바꿈)
 * - 크기
 * - 날짜
 * - 카테고리
 * - 액션 (다운로드/삭제)
 * 
 * @layout
 * - 수평 스크롤 가능 (overflowX: auto)
 * - hover 효과
 */
const FileListView: React.FC<FileListViewProps> = ({
  files,
  selectedFiles,
  onFileSelect,
  onFileClick,
  onFileDownload,
  onFileDelete,
  searchTerm = '',
}) => {
  // 빈 상태
  if (files.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
        }}
      >
        <FolderOpen sx={{ fontSize: 80, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">
          {searchTerm ? '검색 결과가 없습니다' : '파일이 없습니다'}
        </Typography>
        {searchTerm && (
          <Typography variant="body2" color="text.disabled">
            다른 검색어로 시도해보세요
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox />
            </TableCell>
            <TableCell>파일명</TableCell>
            <TableCell align="right">크기</TableCell>
            <TableCell align="center">날짜</TableCell>
            <TableCell align="center">카테고리</TableCell>
            <TableCell align="center">액션</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <TableRow
              key={file.id || ''}
              hover
              sx={{ cursor: 'pointer' }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedFiles.includes(file.id || '')}
                  onChange={(e) => {
                    e.stopPropagation();
                    onFileSelect(file.id || '', e.target.checked);
                  }}
                />
              </TableCell>
              <TableCell onClick={() => onFileClick(file)}>
                <Typography 
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                  }}
                >
                  {file.fileName}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {formatFileSize(file.fileSize)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2">
                  {file.uploadedAt
                    ? (() => {
                        const date =
                          typeof file.uploadedAt === 'object' && 'toDate' in file.uploadedAt
                            ? file.uploadedAt.toDate()
                            : new Date(file.uploadedAt);
                        return date.toLocaleDateString('ko-KR');
                      })()
                    : '-'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={file.category}
                  size="small"
                  sx={{
                    bgcolor: CATEGORY_COLORS[file.category] || '#757575',
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  {onFileDownload && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFileDownload(file);
                      }}
                      title="다운로드"
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  )}
                  {onFileDelete && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFileDelete(file);
                      }}
                      title="삭제"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default FileListView;
